import * as React from "react";

import { usePlpPresenterForTheFilterMenu_Desktop } from "../plp-presenter-for-the-filter-menu-provider";

interface PlpPresenterForTheProductsMasonryConsumerInterface {
  showMasonryWithTwoColumns_onMobile: boolean;
  numberOfColumnsToDisplay: number;

  handleShowMasonryWithOneColumn_onMobile: () => void;
  handleShowMasonryWithTwoColumns_onMobile: () => void;
}

const defaultValue = {};

const PlpPresenterForTheProductsMasonryContext = React.createContext(
  defaultValue as PlpPresenterForTheProductsMasonryConsumerInterface
);

const calculateIsMobile = () => {
  if (window.matchMedia("(max-width: 1023px)").matches) {
    return true;
  }
  return false;
};

const calculateIsTablet = () => {
  if (window.matchMedia("(max-width: 1023px) and (min-width: 768px)").matches) {
    return true;
  }
  return false;
};

const LOCAL_STORAGE_MOBILE_MASONRY =
  "bbnt_plp_mobile_masonry_columns_preference";

type LocalStorageMasonryPreference = {
  twoColumn: boolean;
};

const initialMobileMasonryPreference = false;
const readMobileMasonryPreference = () => {
  // Prevent build error "window is undefined" but keep keep working
  if (typeof window === "undefined") {
    return initialMobileMasonryPreference;
  }

  try {
    const item = window.localStorage.getItem(LOCAL_STORAGE_MOBILE_MASONRY);
    const parsed = item
      ? (JSON.parse(item || "") as LocalStorageMasonryPreference)
      : undefined;

    let value = initialMobileMasonryPreference;
    if (parsed) {
      value = parsed.twoColumn;
    }

    return value;
  } catch (error) {}

  return initialMobileMasonryPreference;
};

export const PlpPresenterForTheProductsMasonryProvider: React.FunctionComponent =
  (props) => {
    const { shouldShowTheFilterMenu_onDesktop } =
      usePlpPresenterForTheFilterMenu_Desktop();

    const [isMobile, setIsMobile] = React.useState(calculateIsMobile());
    const [isTablet, setIsTablet] = React.useState(calculateIsTablet());
    const [
      showMasonryWithTwoColumns_onMobile,
      setShowMasonryWithTwoColumns_onMobile,
    ] = React.useState(readMobileMasonryPreference);
    const [hasInitialized, setHasInitialized] = React.useState(false);

    const numberOfColumnsToDisplay = React.useMemo(() => {
      if (isTablet) {
        return 2;
      } else if (isMobile) {
        if (showMasonryWithTwoColumns_onMobile) {
          return 2;
        }
      } else {
        if (shouldShowTheFilterMenu_onDesktop) {
          return 3;
        } else {
          return 4;
        }
      }
      return 1;
    }, [
      isMobile,
      isTablet,
      showMasonryWithTwoColumns_onMobile,
      shouldShowTheFilterMenu_onDesktop,
    ]);

    React.useEffect(() => {
      const handleResize = () => {
        const isNowOnTablet = calculateIsTablet();
        setIsTablet(isNowOnTablet);

        const isNowOnMobile = calculateIsMobile();
        setIsMobile(isNowOnMobile);
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, []);

    React.useEffect(() => {
      if (hasInitialized) {
        updateMasonryPreferenceInStorage();
      }
      setHasInitialized(true);
    }, [hasInitialized, showMasonryWithTwoColumns_onMobile]);

    const updateMasonryPreferenceInStorage = () => {
      // Prevent build error "window is undefined" but keeps working
      if (typeof window == "undefined") {
        console.warn(
          `Tried setting localStorage key “${LOCAL_STORAGE_MOBILE_MASONRY}” even though environment is not a client`
        );
      }

      try {
        const newValue: LocalStorageMasonryPreference = {
          twoColumn: showMasonryWithTwoColumns_onMobile,
        };

        window.localStorage.setItem(
          LOCAL_STORAGE_MOBILE_MASONRY,
          JSON.stringify(newValue)
        );
      } catch (error) {
        console.warn(
          `Error setting localStorage key “${LOCAL_STORAGE_MOBILE_MASONRY}”:`,
          error
        );
      }
    };

    const handleShowMasonryWithOneColumn_onMobile = React.useCallback(() => {
      setShowMasonryWithTwoColumns_onMobile(false);
      ((window as any).dataLayer as any).push({
        event: "plp_masonry_display_interaction",
        plp: {
          breakpoint: "mobile",
          display: "one_column",
        },
      });
    }, []);
    const handleShowMasonryWithTwoColumns_onMobile = React.useCallback(() => {
      setShowMasonryWithTwoColumns_onMobile(true);
      ((window as any).dataLayer as any).push({
        event: "plp_masonry_display_interaction",
        plp: {
          breakpoint: "mobile",
          display: "two_column",
        },
      });
    }, []);

    const value: PlpPresenterForTheProductsMasonryConsumerInterface =
      React.useMemo(
        () => ({
          showMasonryWithTwoColumns_onMobile,
          numberOfColumnsToDisplay,

          handleShowMasonryWithOneColumn_onMobile,
          handleShowMasonryWithTwoColumns_onMobile,
        }),
        [
          showMasonryWithTwoColumns_onMobile,
          numberOfColumnsToDisplay,

          handleShowMasonryWithOneColumn_onMobile,
          handleShowMasonryWithTwoColumns_onMobile,
        ]
      );

    return (
      <PlpPresenterForTheProductsMasonryContext.Provider value={value}>
        {props?.children}
      </PlpPresenterForTheProductsMasonryContext.Provider>
    );
  };

export const usePlpPresenterForTheProductsMasonry = () => {
  const context = React.useContext(PlpPresenterForTheProductsMasonryContext);
  if (context === defaultValue) {
    throw new Error(
      "usePlpPresenterForTheProductsMasonry must be used within a PlpPresenterForTheProductsMasonryProvider"
    );
  }
  return context;
};
